import type { LocationQueryValue } from "vue-router"
import type { Category } from "~/models/Content/schemas/content-search.schema"
import type {
  Chapter,
  Folder,
  SearchCounts,
  SearchResult,
} from "~/types/contentSearch"
import { useInfiniteQuery, useQuery } from "@tanstack/vue-query"
import { ContentType } from "~/models/Content/ContentType"
import { generateArticlePath } from "~/utilities/urls"

const ARTICLE_TEMPLATE_MAP: Record<string, number[]> = {
  text: [0, 4, 5, 6, 7],
  video: [1],
  quiz: [3],
}

function emptyCounts(): SearchCounts {
  return {
    total: 0,
    locations: new Map<number, number>(),
    templates: new Map<string, number>(),
  }
}

const DEFAULT_LIMIT = 20

export function useContentSearch() {
  const route = useRoute()

  const products = useProductVariantsStore()
  const productContent = useProductContentStore()

  const textFilter = computed(() => route.query.q?.toString() ?? "*")

  const folderFilter = useQueryFilter("folderIds", raw =>
    typeof raw === "string" ? Number(raw) : null)

  const chapterFilter = useQueryFilter("subjectIds", raw =>
    typeof raw === "string" ? Number(raw) : null)

  const categoryFilter = useQueryFilter("articleTemplate", raw =>
    typeof raw === "string" ? raw : null)

  const queryWithTextFilter = computed<IbexaQuery<ContentType.Article>>(() => {
    const pathString = products.activeVariant?.pathString
    return {
      text: textFilter.value,
      template: categoryFilter.values.value.flatMap(c => ARTICLE_TEMPLATE_MAP[c] ?? []),
      fuzziness: 1,
      boost: {
        title: 2,
        label: 1.5,
        body: 1,
      },
      subtree: pathString ? [pathString] : [],
      contentType: [ContentType.Article],
      aggregationFields: ["parentLocationId", "template"],
      aggregationPath: pathString || "/1/2/228/229/",
    }
  })

  const formatter = useSearchFormatter(textFilter)

  const fetchResults = useSearch()

  const countQuery = useQuery({
    queryKey: ["ibexa", "aggregations", queryWithTextFilter] as const,
    async queryFn({ queryKey }) {
      const [, , query] = queryKey

      try {
        const res = await fetchResults({
          ...query,
          limit: 0,
        })

        const counts = emptyCounts()

        counts.total = res.count

        const locations = res.aggregations?.parentLocationId ?? []
        for (const { key, count } of locations) {
          counts.locations.set(Number(key), count)
        }

        const templates = res.aggregations?.template ?? []
        for (const { key, count } of templates) {
          let template = "text"
          if (key === "quiz" || key === "video") {
            template = key
          }

          // TOOD: The assumption is that every entry in the list has a unique key.
          const existing = counts.templates.get(template) ?? 0

          counts.templates.set(template, existing + count)
        }

        return counts
      } catch (error) {
        captureException(error, {
          extra: {
            query,
          },
        })

        return emptyCounts()
      }
    },
  })

  const totalCounts = computed<SearchCounts>(() => countQuery.data.value ?? emptyCounts())

  const parentLocationIdFilter = computed(() => {
    const folders = folderFilter.values.value
    const chapters = chapterFilter.values.value

    if (productContent.folders.length > 0) {
      /*
      * If the product has folders, selecting a folder includes all subjects in that folder.
      * If we select subjects inside a folder, the other un-selected subjects inside it are not included in the query.
      * When we select subjects inside multiple folders, all the selected subjects from the different folders are included in the query.
      * */
      return productContent.folders.flatMap((f) => {
        if (folders.length > 0 && !folders.includes(f.id)) {
          return []
        }

        const ids = f.children?.filter(isContentType(ContentType.Chapter)).map(s => s.id) ?? []

        if (chapters.length === 0) {
          return ids
        }

        const selectedInFolder = chapters.filter(c => ids.includes(c))

        return selectedInFolder.length === 0 ? ids : selectedInFolder
      })
    } else {
      return productContent.subjects.filter(s => chapters.length === 0 || chapters.includes(s.id)).map(s => s.id)
    }
  })

  const infiniteQuery = useInfiniteQuery({
    queryKey: [
      "ibexa",
      "search",
      queryWithTextFilter,
      parentLocationIdFilter,
    ] as const,
    enabled: () => !!products.activeVariant?.pathString,
    initialPageParam: {
      offset: 0,
    },
    async queryFn({ pageParam, queryKey }) {
      const [, , unfilteredQuery, parentLocationId] = queryKey

      const query: IbexaQuery<ContentType.Article> = {
        ...unfilteredQuery,
        parentLocationId,
        limit: DEFAULT_LIMIT,
        offset: pageParam.offset,
      }

      try {
        return await fetchResults<ContentType.Article>(query)
      } catch (error) {
        captureException(error, {
          extra: {
            query,
          },
        })

        return {
          data: [],
          count: 0,
        }
      }
    },

    getNextPageParam: (prevPage, _, prevParams) => {
      const offset = prevParams.offset + prevPage.data.length
      return offset < prevPage.count
        ? {
            offset,
          }
        : null
    },
  })

  const results = computed(() =>
    (infiniteQuery.data.value?.pages ?? []).reduce<{ data: SearchResult[], count: SearchCounts }>(
      (acc, page, i) => {
        acc.count.total ||= page.count

        if (i === 0) {
          // The aggregations are the same for all pages, so we only count on the first iteration
          const locations = page.aggregations?.parentLocationId ?? []
          for (const { key, count } of locations) {
            acc.count.locations.set(Number(key), count)
          }

          const templates = page.aggregations?.template ?? []
          for (const { key, count } of templates) {
            let template = "text"
            if (key === "quiz" || key === "video") {
              template = key
            }

            // TODO: The assumption is that every entry in the list has a unique key.
            const existing = acc.count.templates.get(template) ?? 0

            acc.count.templates.set(template, existing + count)
          }
        }

        for (const article of page.data) {
          const subject = productContent.subjects.find(
            s => s.id === article.parentLocationId,
          )

          const folder = productContent.folders.find(f => f.id === subject?.parentLocationId)
          const hasAccess = products.activeVariant?.userHasLicence ? true : !!subject?.isFree

          acc.data.push({
            id: article.id,
            article,
            link: hasAccess && article.parentLocationId
              ? {
                  path: generateArticlePath(
                    products.activeProduct?.productKey,
                    products.activeVariant?.variantKey,
                    subject?.title || "",
                    article.title,
                  ),
                  query: {
                    artikkel: article.id,
                    kapittel: article.parentLocationId,
                  },
                }
              : undefined,
            title: formatter.title(article.title),
            description: formatter.description(article.body?.html),
            subject: {
              title: subject?.title || "",
              priority: subject?.priority || 0,
            },
            folder: folder
              ? {
                  title: folder.title || "",
                  priority: folder.priority || 0,
                }
              : undefined,
            hasAccess,
          })
        }

        return acc
      },
      {
        data: [],
        count: emptyCounts(),
      },
    ),
  )

  const categories = computed<Category[]>(() => {
    const hasChapterFilter = chapterFilter.values.value.length > 0
    const hasFolderFilter = folderFilter.values.value.length > 0

    const count = (hasChapterFilter || hasFolderFilter) ? results.value.count : totalCounts.value

    function createCategoryFilter(key: "text" | "video" | "quiz", label: string) {
      return {
        id: key,
        title: label,
        active: categoryFilter.values.value.includes(key),
        results: count.templates.get(key) ?? 0,
      }
    }

    return [
      createCategoryFilter("text", "Artikkel"),
      createCategoryFilter("quiz", "Test deg selv"),
      createCategoryFilter("video", "Video"),
    ]
  })

  const chapters = computed<Chapter[]>(() =>
    productContent.subjects.map((subject) => {
      const active = chapterFilter.values.value.includes(subject.id)
      return {
        id: subject.id,
        title: subject.title,
        hits: subject.totalChildren ?? 0,
        active,
        results: active ? (results.value?.count.locations.get(subject.id) ?? 0) : (totalCounts.value?.locations.get(subject.id) ?? 0),
        parentLocationId: subject.parentLocationId,
        priority: subject.priority,
        isFree: subject.isFree,
      } satisfies Chapter
    }),
  )

  const folders = computed<Folder[]>(() =>
    productContent.folders.map(folder => ({
      id: folder.id,
      title: folder.title,
      active: folderFilter.values.value.includes(folder.id),
      results: (totalCounts.value?.locations.get(folder.id) ?? 0),
      priority: folder.priority,
      subjectIds: chapters.value.filter(chapter => chapter.parentLocationId === folder.id).map(chapter => chapter.id),
    })),
  )

  function toggleChapter(chapterId: number) {
    const updatedChapters = chapterFilter.toggleParam(chapterId)
    return navigateTo({
      query: {
        ...route.query,
        ...updatedChapters,
      },
    }, {
      replace: true,
    })
  }

  function toggleCategory(categoryId: string) {
    const updatedCategories = categoryFilter.toggleParam(categoryId)
    return navigateTo({
      query: {
        ...route.query,
        ...updatedCategories,
      },
    }, {
      replace: true,
    })
  }

  function toggleFolder(folderId: number) {
    const updatedFolders = folderFilter.toggleParam(folderId)
    const isFolderSelected = updatedFolders.folderIds?.includes(folderId)

    if (!isFolderSelected) {
      // When the folder is deselected, find subjects in folder
      const subjectsInFolder = folders.value.find(f => f.id === folderId)?.subjectIds ?? []

      const toggled = chapterFilter.values.value.filter(id => subjectsInFolder.includes(id))

      let subjectIds = chapterFilter.values.value
      for (const subjectId of toggled) {
        subjectIds = chapterFilter.toggleParam(subjectId, subjectIds).subjectIds ?? []
      }

      return navigateTo({
        query: {
          ...route.query,
          ...updatedFolders,
          subjectIds,
        },
      }, {
        replace: true,
      })
    } else {
      return navigateTo({
        query: {
          ...route.query,
          ...updatedFolders,
        },
      }, {
        replace: true,
      })
    }
  }

  return {
    loading: infiniteQuery.isFetching,
    totalCounts,
    filteredCounts: computed(() => infiniteQuery.data.value?.pages[0]?.count ?? 0),
    results,
    loadMore: () => infiniteQuery.fetchNextPage(),
    categories,
    chapters,
    folders,
    toggleCategory,
    toggleChapter,
    toggleFolder,
  }
}

function useQueryFilter<T extends string | number>(
  key: string,
  convert: (raw: LocationQueryValue) => T | null,
) {
  const route = useRoute()

  const values = computed(() => {
    const raw = route.query[key]

    if (Array.isArray(raw)) {
      return raw
        .map(convert)
        .filter(
          (it, i, arr): it is T =>
            it !== null && arr.indexOf(it) === i,
        )
    } else if (raw) {
      const sanitized = convert(raw)
      return sanitized ? [sanitized] : []
    } else {
      return []
    }
  })

  return {
    values,
    toggleParam(raw: T, existingValues = values.value) {
      const newValues = existingValues.includes(raw)
        ? existingValues.filter(it => it !== raw)
        : [...existingValues, raw]

      return {
        [key]: newValues,
      }
    },
  }
}
